import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, type AvatarProps, Box, Grid, type SxProps, type Theme, Tooltip, Typography, useTheme } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Portal from '@mui/material/Portal';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '@/api/mutations/useAuthMutations';
import { ModalWindow } from '@/components/Modal/Modal';
import { styles } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { paths } from '@/constants';
import { snackbarColor } from '@/enums';
import { useColorMode, useSnackbar } from '@/hooks';
import { useChangeUser } from '@/preferences';
import { isApiError } from '@/types';

interface Props {
    id?: AvatarProps['id'];
    alt: AvatarProps['alt'];
    src: AvatarProps['src'];
    title: AvatarProps['title'];
    margin?: string;
    height?: string;
    width?: string;
    cursor?: string;
    border?: (theme: Theme) => string;
    onClick?: () => void;
    variant?: AvatarProps['variant'];
    children?: ReactNode;
    sx?: SxProps<Theme>;
    backgroundColor?: string;
}

export const Image = (props: Props) => {
    const { sx } = props;
    const {
        height = '42px',
        width = '42px',
        id = '',
        margin = '0 15px 0 0',
        cursor = 'default',
        variant = 'circular',
        border = 'none',
        backgroundColor,
        onClick,
    } = props;

    return (
        <Avatar
            id={id}
            alt={props.alt}
            src={props.src}
            title={props.title}
            onClick={onClick}
            variant={variant}
            sx={Object.assign(
                {},
                {
                    width,
                    height,
                    margin,
                    cursor,
                    alignItems: 'center',
                    display: 'inline-flex',
                    border,
                    backgroundColor,
                },
                sx,
            )}
            children={props.children}
        />
    );
};

export function ImageAvatar(props: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const changeUser = useChangeUser();
    const showSnackbar = useSnackbar();
    const [open, setOpen] = useState(false);
    const { mutateAsync: logOut, status } = useLogout();
    const [hover, setHover] = useState(false);
    const isDarkMode = useColorMode();
    const theme = useTheme();

    const handleLogout = async () => {
        try {
            const { success } = await logOut();
            if (success) {
                changeUser({ last_page: location.pathname + location.search });
            }
        } catch (error) {
            if (isApiError(error)) {
                showSnackbar({
                    open: true,
                    message: error.data.message,
                    severity: snackbarColor.error,
                });
            }
        }
    };

    const ProfileMenu = () => (
        <Box
            sx={{
                borderRadius: '8px',
                background: theme => theme.palette.secondary.light,
                minWidth: '190px',
            }}
        >
            <div className="flex flex-wrap w-full flex-col">
                <Grid sx={[styles.iconTextRowMenu]} container onClick={() => navigate(paths.profile.index)}>
                    <PersonIcon
                        sx={{
                            color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        children={t('profile.profile')}
                        color={theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22)}
                        variant="body2"
                    />
                </Grid>
                <Grid sx={[styles.iconTextRowMenu]} container onClick={() => setOpen(true)}>
                    <LogoutIcon
                        sx={{
                            color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        children={t('log-out')}
                        color={theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22)}
                        variant="body2"
                    />
                </Grid>
            </div>
        </Box>
    );

    return (
        <>
            <Tooltip
                title={<ProfileMenu />}
                placement="top-start"
                slotProps={{
                    tooltip: {
                        sx: {
                            borderRadius: '8px',
                            margin: '0 0 10px -2px !important',
                            padding: '0',
                            background: theme => theme.palette.background.paper,
                        },
                    },
                }}
            >
                <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="flex items-center relative">
                    <Image
                        {...props}
                        cursor="pointer"
                        border={theme => (hover ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent')}
                        onClick={() => navigate(paths.profile.index)}
                        backgroundColor={location.pathname === paths.profile.index ? theme.palette.text.secondary : theme.custom.c5}
                    />

                    {status === 'pending' && (
                        <Portal>
                            <Backdrop open={true}>
                                <Spinner size="1.5em" className="text-secondary" />
                            </Backdrop>
                        </Portal>
                    )}
                </div>
            </Tooltip>

            <ModalWindow open={open} handleClose={() => setOpen(prev => !prev)} styles={Object.assign(styles.modal)}>
                <div className="flex flex-wrap w-full justify-center">
                    <div className="flex flex-wrap w-full md:grow-0 md:basis-[100%] md:max-w-[100%] text-center justify-center items-center">
                        <div className="flex flex-wrap w-full md:grow-0 md:basis-[100%] md:max-w-[100%] pb-[40px] justify-center mt-[30vh]">
                            <div className="md:grow-0 md:basis-[50%] md:max-w-[50%]">
                                <Text variant="h2" color="text-primary" children={t('want-logout')} className="mt-[20px]" />
                                <div className="flex flex-col gap-4 items-center mt-[48px]">
                                    <Button size="large" className="w-[320px]" onClick={() => setOpen(prev => !prev)} variant="contained">
                                        {t('buttons.no-logout')}
                                    </Button>

                                    <Button variant="outlined" size="large" className="w-[320px]" onClick={handleLogout}>
                                        {t('log-out')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalWindow>
        </>
    );
}
